import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';
import { useTranslate } from '../../Hook/useTranslate';
import { Loader2, Lock, Eye, EyeOff, ArrowLeft, ShieldCheck } from 'lucide-react';
import './Profile.css'; // Reusing some profile styles

const ChangePassword = () => {
    const t = useTranslate();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPass, setShowPass] = useState({ current: false, new: false, confirm: false });
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [message, setMessage] = useState({ type: '', text: '' });

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.currentPassword.trim()) {
            setMessage({ type: 'error', text: 'Current password is required' });
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setMessage({ type: 'error', text: 'New passwords do not match' });
            return;
        }

        if (formData.newPassword.length < 6) {
            setMessage({ type: 'error', text: 'New password must be at least 6 characters long' });
            return;
        }

        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const res = await api.put('/user/change-password', {
                currentPassword: formData.currentPassword,
                newPassword: formData.newPassword
            });

            setMessage({ type: 'success', text: res.data.message });
            setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            
            // Navigate back after 2 seconds
            setTimeout(() => {
                navigate('/profile');
            }, 2000);

        } catch (err) {
            setMessage({ 
                type: 'error', 
                text: err.response?.data?.message || 'Error updating password' 
            });
        } finally {
            setLoading(false);
        }
    };

    const toggleShow = (field) => {
        setShowPass(prev => ({ ...prev, [field]: !prev[field] }));
    };

    return (
        <div className="profile-edit-page">
            <div className="profile-header-bar">
                <button onClick={() => navigate(-1)} className="back-btn">
                    <ArrowLeft size={20} />
                </button>
                <h1>Change Password</h1>
                <div style={{ width: 40 }}></div>
            </div>

            <div className="profile-container">
                <div className="profile-form">
                    <div className="avatar-section" style={{ flexDirection: 'column', gap: '15px' }}>
                        <div className="avatar-wrapper" style={{ height: 80, width: 80, background: '#f0fdf4', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', margin: '0 auto', boxShadow: '0 4px 12px rgba(22, 163, 74, 0.1)' }}>
                            <ShieldCheck size={40} />
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <h3 style={{ margin: '0 0 5px', fontSize: '18px', fontWeight: '800', color: '#1e293b' }}>Security Settings</h3>
                            <p style={{ margin: 0, fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Update your password to keep your account secure</p>
                        </div>
                    </div>

                    {message.text && (
                        <div className={`message-alert ${message.type}`} style={{ margin: '10px 0' }}>
                            <span>{message.text}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="form-sections">
                        <div className="input-group">
                            <label><Lock size={16} /> Current Password</label>
                            <div style={{ position: 'relative' }}>
                                <input 
                                    type={showPass.current ? "text" : "password"} 
                                    value={formData.currentPassword}
                                    onChange={(e) => setFormData({...formData, currentPassword: e.target.value})}
                                    placeholder="Enter current password"
                                    required
                                    style={{ paddingRight: '50px' }}
                                />
                                <button 
                                    type="button" 
                                    onClick={() => toggleShow('current')}
                                    style={{ position: 'absolute', right: '15px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center' }}
                                >
                                    {showPass.current ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        <div className="input-group">
                            <label><Lock size={16} /> New Password</label>
                            <div style={{ position: 'relative' }}>
                                <input 
                                    type={showPass.new ? "text" : "password"} 
                                    value={formData.newPassword}
                                    onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
                                    placeholder="Minimum 6 characters"
                                    required
                                    style={{ paddingRight: '50px' }}
                                />
                                <button 
                                    type="button" 
                                    onClick={() => toggleShow('new')}
                                    style={{ position: 'absolute', right: '15px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center' }}
                                >
                                    {showPass.new ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        <div className="input-group">
                            <label><Lock size={16} /> Confirm New Password</label>
                            <div style={{ position: 'relative' }}>
                                <input 
                                    type={showPass.confirm ? "text" : "password"} 
                                    value={formData.confirmPassword}
                                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                                    placeholder="Repeat new password"
                                    required
                                    style={{ paddingRight: '50px' }}
                                />
                                <button 
                                    type="button" 
                                    onClick={() => toggleShow('confirm')}
                                    style={{ position: 'absolute', right: '15px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center' }}
                                >
                                    {showPass.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        <button type="submit" className="save-btn" disabled={loading}>
                            {loading ? <Loader2 className="spinner" size={18} /> : <Lock size={18} />}
                            {loading ? 'Updating...' : 'Update Password'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;
